import { useQuery } from '@tanstack/react-query';

import { useIsStargazer } from 'hooks/useIsStargazer';
import http from 'services/http';
import { checkIsLogin } from 'utils/auth';

/**
 * 별지기 MG(미니멈 개런티) 산정 응답 타입과 조회 훅 (#2182).
 *
 * DTO 를 그대로 화면에 흘려보내므로 BE 필드명이 바뀌면 이 파일과 섹션 컴포넌트를 함께 고친다.
 * 차액은 BE 응답을 그대로 쓴다 — FE 에서 `MG − 총리워드` 를 재계산하면 반올림·세전세후 기준 차이로
 * 어드민 숫자와 어긋나고, 그건 정책 C 의 "화면과 어드민의 당월 총 리워드가 같을 것"을 깬다.
 *
 * **차액 산정 기준은 세전이다** (2026-09-14, BE 구현 기준). 계약서 제5조의5 (4) 가 "MG 보정액·출금액 및
 * 세금은 당월 총 리워드에 포함하지 아니한다" 로 정하고 있고, 이는 세금 항목을 집계에서 빼라는
 * 뜻이지 세금을 공제하라는 뜻이 아니다. 원장에 세금 항목이 없고 3.3% 는 출금 시 원천징수라
 * 집계 대상이 아니다. 따라서 `shortfall = max(MG − preTaxAmount, 0)` 이며 어드민도 같은 값을 쓴다.
 * 세후 기준으로 잡으면 실수령이 세전·세후 어느 보장액과도 맞지 않는다.
 */

export const MINIMUM_GUARANTEE_KEY = {
  all: ['minimumGuarantee'],
  detail: () => [...MINIMUM_GUARANTEE_KEY.all, 'detail'],
};

/** 세전·세후를 각각 표기한다 (기획 확인 3번 회신) */
export interface MinimumGuaranteeRevenue {
  preTaxAmount: number;
  preTaxAmountFormat: string;
  afterTaxAmount: number;
  afterTaxAmountFormat: string;
}

export interface MinimumGuaranteeSettlement {
  /** 대상 월 (예: '2026-09') */
  month: string;
  /** 산정 시작 — UTC ISO. KST 1일 00:00 */
  startAt: string;
  /** 산정 종료 — UTC ISO. KST 말일 23:59 */
  endAt: string;
  /**
   * 지급 예정일 (YYYY-MM-DD). **주말·공휴일 자동 보정은 없는 명목일이다.**
   * 계약서 제5조의5 (3) 이 "갑이 지급일을 변경할 수 있다" 로만 정해 BE 가 날짜를 옮기지 않는다.
   * 실제 변경 가능성은 안내 문구(`chat.rewards_mg_notice`)가 알린다.
   */
  payoutDate: string;
}

export interface MinimumGuaranteeContract {
  /** 개별 계약 MG 금액 */
  amount: number;
  amountFormat: string;
  /** 차액 = max(MG − 당월 총 리워드, 0) */
  shortfall: number;
  shortfallFormat: string;
}

/**
 * 전월 확정분. KST 1~10일에만 객체이고 그 외에는 null.
 * `isFinalized` 는 말일 24:00 에서 집계 배치 한 바퀴(약 1시간)가 지난 뒤 true 가 된다.
 *
 * **자기 `settlement` 을 따로 갖는다.** 최상위 `settlement` 은 당월분이라 지급일이 다음 달 10일인데,
 * 1~10일에 실제로 돈이 나가는 건 전월분(이번 달 10일)이다. 한 화면에 두 지급일이 섞이면
 * 크리에이터가 지급일을 한 달 틀리게 읽는다.
 */
export type MinimumGuaranteePreviousMonth = MinimumGuaranteeRevenue & {
  settlement: MinimumGuaranteeSettlement;
  isFinalized: boolean;
};

export interface MinimumGuaranteeResponseDto {
  /** 당월분 산정 정보. `payoutDate` 는 다음 달 10일이다 */
  settlement: MinimumGuaranteeSettlement;
  /** 당월 진행분 (running total). 시간 단위 집계라 최대 약 1시간 늦게 반영된다 */
  currentMonth: MinimumGuaranteeRevenue;
  previousMonth: MinimumGuaranteePreviousMonth | null;
  /**
   * 어드민에 MG 가 입력되지 않은 계정이면 null.
   * **1차에는 항상 null 이다** — 숫자 노출이 후속으로 밀려(기획 확인 2번), FE 플래그로만 숨기면
   * 개발자도구에서 금액이 보이므로 BE 가 응답 단계에서 비운다(BE 확정 코멘트, MR !1195).
   * **노출 시점은 BE 가 정한다** — 공개가 확정돼 이 필드가 채워지면 화면은 FE 배포 없이 바로 그린다.
   */
  minimumGuarantee: MinimumGuaranteeContract | null;
}

export const useGetMinimumGuarantee = () => {
  const { isStargazer } = useIsStargazer();

  return useQuery({
    queryKey: MINIMUM_GUARANTEE_KEY.detail(),
    queryFn: () =>
      // useErrorToast: false — 실패해도 토스트를 띄우지 않는다. MG 섹션은 대시보드의 부가 정보라
      // 조회가 안 되면 섹션만 조용히 감추는 쪽이 맞다 (정책 C).
      http.get<MinimumGuaranteeResponseDto>('/affiliate/v1/revenues/minimum-guarantee', {}, { useErrorToast: false }),
    // 별지기가 아니면 부르지 않는다. 일반 크리에이터에게 403 을 만들 이유가 없다.
    enabled: checkIsLogin() && isStargazer,
    // GlobalProvider 의 전역 기본값(throwOnError: true)을 끈다. 켜두면 이 훅의 실패가
    // 에러바운더리로 승격돼 대시보드 최상단이 "홈으로 돌아가기" 패널로 덮인다.
    throwOnError: false,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
  });
};
